import React, { useMemo } from 'react';
import { PlusIcon, TrashIcon, PresentationChartBarIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Block, isPrestation } from '../../types';
import { BlockCalculationService } from '../../services';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Comment le montant de cette ligne est réparti dans l'export. */
export type DistributionMode = 'export' | 'autre' | 'eclater';

const DISTRIBUTION_OPTIONS: { value: DistributionMode; label: string; title: string }[] = [
  { value: 'export',  label: 'I',   title: 'Afficher comme ligne distincte dans l\'export' },
  { value: 'autre',   label: 'II',  title: 'Regrouper dans une ligne "Autre" de chiffrage' },
  { value: 'eclater', label: 'III', title: 'Éclater proportionnellement dans tous les prix unitaires' },
];

export interface VenteLine {
  id: string;
  label: string;
  preset: boolean;
  valueType: 'percent' | 'fixed';
  value: number;
  distributionMode: DistributionMode;
}

export interface FeuilleDeVenteState {
  coefficient: number;
  coefficientMode: DistributionMode;
  margeObjectif: number;
  tauxHoraire: number;        // €/h — base du calcul de main d'œuvre sur les lignes ventilées
  tempsApproDefault: number;  // h/ligne — forfait d'approvisionnement par défaut (override possible par ligne)
  /** Coef multiplicateur par défaut appliqué au PU de chaque ligne (override possible par ligne). */
  coefDefaut: number;
  /** Frais de chantier par défaut (%, installation/accès/pénibilité) — niveau affaire. */
  fraisChantierDefaut: number;
  /** Frais généraux par défaut (%, structure entreprise, typiquement 8-12%) — niveau affaire. */
  fraisGenerauxDefaut: number;
  /** Marge commerciale cible par défaut (%) — niveau affaire. */
  margeDefaut: number;
  lines: VenteLine[];
}

export const DEFAULT_FEUILLE_DE_VENTE: FeuilleDeVenteState = {
  coefficient: 1,
  coefficientMode: 'export',
  margeObjectif: 15,
  tauxHoraire: 45,
  tempsApproDefault: 0.5,
  coefDefaut: 1.3,
  fraisChantierDefaut: 5,
  fraisGenerauxDefaut: 10,
  margeDefaut: 10,
  lines: [
    { id: 'frais-generaux',  label: 'Frais généraux',  preset: true,  valueType: 'percent', value: 8,  distributionMode: 'export' },
    { id: 'benefice',        label: 'Bénéfice',         preset: true,  valueType: 'percent', value: 10, distributionMode: 'export' },
    { id: 'aleas',           label: 'Aléas',            preset: true,  valueType: 'percent', value: 3,  distributionMode: 'export' },
    { id: 'cout-indirect',   label: 'Coût indirect',    preset: false, valueType: 'percent', value: 0,  distributionMode: 'eclater' },
  ],
};

// Garantit qu'un FeuilleDeVenteState chargé depuis un SavedChiffrage ancien possède les nouveaux champs.
export const normalizeFeuilleDeVente = (state: FeuilleDeVenteState): FeuilleDeVenteState => ({
  ...DEFAULT_FEUILLE_DE_VENTE,
  ...state,
  tauxHoraire: state.tauxHoraire ?? DEFAULT_FEUILLE_DE_VENTE.tauxHoraire,
  tempsApproDefault: state.tempsApproDefault ?? DEFAULT_FEUILLE_DE_VENTE.tempsApproDefault,
  coefDefaut: state.coefDefaut ?? DEFAULT_FEUILLE_DE_VENTE.coefDefaut,
  fraisChantierDefaut: state.fraisChantierDefaut ?? DEFAULT_FEUILLE_DE_VENTE.fraisChantierDefaut,
  fraisGenerauxDefaut: state.fraisGenerauxDefaut ?? DEFAULT_FEUILLE_DE_VENTE.fraisGenerauxDefaut,
  margeDefaut: state.margeDefaut ?? DEFAULT_FEUILLE_DE_VENTE.margeDefaut,
});

// ─── Props ────────────────────────────────────────────────────────────────────

interface FeuilleDeVentePanelProps {
  blocks: Block[];
  surfaceTraitee: string;
  budgetTotal: string;
  state: FeuilleDeVenteState;
  onChange: (state: FeuilleDeVenteState) => void;
}

const fmt = (n: number) => BlockCalculationService.formatPrice(n);

// ─── Sélecteur I / II / III ──────────────────────────────────────────────────

const DistributionSelector: React.FC<{
  value: DistributionMode;
  onChange: (mode: DistributionMode) => void;
}> = ({ value, onChange }) => (
  <div className="flex items-center flex-shrink-0">
    {DISTRIBUTION_OPTIONS.map(opt => (
      <button
        key={opt.value}
        title={opt.title}
        onClick={() => onChange(opt.value)}
        className={`w-3.5 h-3.5 text-[7px] font-bold flex items-center justify-center transition-colors first:rounded-l last:rounded-r border-y border-r first:border-l ${
          value === opt.value
            ? 'bg-violet-500 text-white border-violet-500 z-10'
            : 'bg-white text-gray-300 border-gray-200 hover:bg-gray-50 hover:text-gray-500'
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

// ─── Composant ────────────────────────────────────────────────────────────────

const FeuilleDeVentePanel: React.FC<FeuilleDeVentePanelProps> = ({
  blocks,
  surfaceTraitee,
  budgetTotal,
  state,
  onChange,
}) => {
  const coutDeRevient = useMemo(() => (
    blocks
      .filter(b => isPrestation(b) && !b.content.isVentilationCostLine)
      .reduce((sum, b) => sum + BlockCalculationService.calculatePrestationTotal(b), 0)
  ), [blocks]);

  const { lineAmounts, prixVente } = useMemo(() => {
    let running = coutDeRevient * state.coefficient;
    const amounts = state.lines.map(line => {
      const amount = line.valueType === 'percent'
        ? running * (line.value / 100)
        : line.value;
      running += amount;
      return amount;
    });
    return { lineAmounts: amounts, prixVente: running };
  }, [coutDeRevient, state]);

  const surface    = parseFloat(surfaceTraitee.replace(',', '.')) || 0;
  const budgetNum  = BlockCalculationService.parsePrice(budgetTotal);
  const ratioRevient = surface > 0 ? coutDeRevient / surface : null;
  const ratioVente   = surface > 0 ? prixVente / surface : null;
  const marge        = prixVente - coutDeRevient;
  const margePct     = prixVente > 0 ? (marge / prixVente) * 100 : 0;
  const ecartBudget  = budgetNum > 0 ? prixVente - budgetNum : null;
  const margeDelta   = margePct - state.margeObjectif;

  const updateLine = (id: string, updates: Partial<VenteLine>) =>
    onChange({ ...state, lines: state.lines.map(l => l.id === id ? { ...l, ...updates } : l) });

  const removeLine = (id: string) =>
    onChange({ ...state, lines: state.lines.filter(l => l.id !== id) });

  const addLine = () =>
    onChange({
      ...state,
      lines: [...state.lines, {
        id: `line-${Date.now()}`, label: 'Nouvelle ligne', preset: false,
        valueType: 'percent', value: 0, distributionMode: 'export',
      }],
    });

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-4">

      {/* En-tête */}
      <div className="px-4 py-3 bg-violet-50 border-b border-violet-100 flex items-center gap-2">
        <PresentationChartBarIcon className="w-4 h-4 text-violet-600 flex-shrink-0" />
        <span className="text-xs font-semibold text-violet-700 uppercase tracking-wider">
          Feuille de vente
        </span>
      </div>

      {/* Macro données */}
      <div className="px-4 py-3 border-b border-gray-100 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Coût de revient</span>
          <span className="text-sm font-semibold text-gray-800">{fmt(coutDeRevient)} €</span>
        </div>
        {ratioRevient !== null && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Ratio brut</span>
            <span className="text-xs text-gray-500 tabular-nums">{fmt(ratioRevient)} €/m²</span>
          </div>
        )}
      </div>

      {/* Paramètres de coût (MO) */}
      <div className="px-4 py-3 border-b border-gray-100 space-y-2">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Paramètres de coût</p>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600 flex-1 min-w-0 truncate" title="Taux horaire utilisé pour calculer le coût de main d'œuvre des lignes ventilées">
            Taux horaire
          </label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={state.tauxHoraire}
            onChange={e => onChange({ ...state, tauxHoraire: parseFloat(e.target.value) || 0 })}
            className="w-16 px-2 py-1 text-sm border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
          <span className="text-xs text-gray-400 w-8 flex-shrink-0">€/h</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600 flex-1 min-w-0 truncate" title="Forfait d'approvisionnement par défaut appliqué sur chaque ligne ventilée (modifiable par ligne)">
            Temps appro défaut
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={state.tempsApproDefault}
            onChange={e => onChange({ ...state, tempsApproDefault: parseFloat(e.target.value) || 0 })}
            className="w-16 px-2 py-1 text-sm border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
          <span className="text-xs text-gray-400 w-8 flex-shrink-0">h/ligne</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600 flex-1 min-w-0 truncate" title="Multiplicateur appliqué au PU de chaque ligne pour obtenir le total (modifiable par ligne)">
            Coef défaut
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={state.coefDefaut}
            onChange={e => onChange({ ...state, coefDefaut: parseFloat(e.target.value) || 0 })}
            className="w-16 px-2 py-1 text-sm border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
          <span className="text-xs text-gray-400 w-8 flex-shrink-0">×</span>
        </div>
      </div>

      {/* Paramètres niveau affaire (conjoncturels, non remontés en biblio) */}
      <div className="px-4 py-3 border-b border-gray-100 space-y-2">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" title="Coefs appliqués globalement au chiffrage — non remontés en biblio (conjoncturels : client, projet, année)">
          Paramètres affaire
        </p>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600 flex-1 min-w-0 truncate" title="Frais chantier : installation, accès, pénibilité — appliqués globalement">
            Frais chantier
          </label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={state.fraisChantierDefaut}
            onChange={e => onChange({ ...state, fraisChantierDefaut: parseFloat(e.target.value) || 0 })}
            className="w-16 px-2 py-1 text-sm border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
          <span className="text-xs text-gray-400 w-8 flex-shrink-0">%</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600 flex-1 min-w-0 truncate" title="Frais généraux : structure entreprise — typiquement 8-12%">
            Frais généraux
          </label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={state.fraisGenerauxDefaut}
            onChange={e => onChange({ ...state, fraisGenerauxDefaut: parseFloat(e.target.value) || 0 })}
            className="w-16 px-2 py-1 text-sm border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
          <span className="text-xs text-gray-400 w-8 flex-shrink-0">%</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600 flex-1 min-w-0 truncate" title="Marge commerciale cible par défaut">
            Marge
          </label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={state.margeDefaut}
            onChange={e => onChange({ ...state, margeDefaut: parseFloat(e.target.value) || 0 })}
            className="w-16 px-2 py-1 text-sm border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
          <span className="text-xs text-gray-400 w-8 flex-shrink-0">%</span>
        </div>
      </div>

      {/* Coefficient global */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <DistributionSelector
            value={state.coefficientMode}
            onChange={mode => onChange({ ...state, coefficientMode: mode })}
          />
          <label className="text-xs text-gray-600 flex-1 min-w-0 truncate">Coefficient global</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={state.coefficient}
            onChange={e => onChange({ ...state, coefficient: parseFloat(e.target.value) || 1 })}
            className="w-16 px-2 py-1 text-sm border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
        </div>
        {state.coefficient !== 1 && (
          <p className="text-xs text-gray-400 mt-1 text-right tabular-nums">
            Base → {fmt(coutDeRevient * state.coefficient)} €
          </p>
        )}
      </div>

      {/* Lignes de marge */}
      <div className="px-4 py-3 border-b border-gray-100 space-y-3">
        {state.lines.map((line, i) => {
          const amount = lineAmounts[i] ?? 0;
          return (
            <div key={line.id}>
              <div className="flex items-center gap-1.5">
                {/* Sélecteur I/II/III */}
                <DistributionSelector
                  value={line.distributionMode}
                  onChange={mode => updateLine(line.id, { distributionMode: mode })}
                />
                {/* Label */}
                <input
                  type="text"
                  value={line.label}
                  onChange={e => updateLine(line.id, { label: e.target.value })}
                  className="flex-1 text-xs text-gray-700 bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-violet-400 focus:outline-none py-0.5 min-w-0"
                />
                {/* Valeur */}
                <input
                  type="number"
                  step={line.valueType === 'percent' ? '0.5' : '100'}
                  min="0"
                  value={line.value}
                  onChange={e => updateLine(line.id, { value: parseFloat(e.target.value) || 0 })}
                  className="w-12 px-1.5 py-0.5 text-xs border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-400"
                />
                {/* Toggle % / € */}
                <button
                  onClick={() => updateLine(line.id, { valueType: line.valueType === 'percent' ? 'fixed' : 'percent' })}
                  className={`w-6 h-5 text-xs rounded flex-shrink-0 flex items-center justify-center font-medium transition-colors ${
                    line.valueType === 'percent'
                      ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {line.valueType === 'percent' ? '%' : '€'}
                </button>
                {/* Supprimer */}
                {!line.preset ? (
                  <button onClick={() => removeLine(line.id)} className="flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors">
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <div className="w-3.5 flex-shrink-0" />
                )}
              </div>
              <div className="flex justify-end mt-0.5">
                <span className="text-xs text-gray-400 tabular-nums">+ {fmt(amount)} €</span>
              </div>
            </div>
          );
        })}

        <button
          onClick={addLine}
          className="w-full flex items-center gap-1.5 py-1 text-xs text-gray-400 hover:text-violet-600 transition-colors"
        >
          <PlusIcon className="w-3.5 h-3.5 flex-shrink-0" />
          Ajouter une ligne
        </button>
      </div>

      {/* Prix de vente + indicateurs */}
      <div className="px-4 py-4 bg-gray-50 space-y-3">

        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Prix de vente HT
          </p>
          <div className="text-right">
            <span className="text-2xl font-bold text-violet-700 tabular-nums">{fmt(prixVente)}</span>
            <span className="text-sm text-violet-500 ml-1">€</span>
          </div>
          {ratioVente !== null && (
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-xs text-gray-400">Ratio vente</span>
              <span className="text-xs font-medium text-gray-600 tabular-nums">{fmt(ratioVente)} €/m²</span>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200" />

        {/* Marge réelle + objectif */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Marge réalisée</span>
            <span className={`text-xs font-semibold tabular-nums ${marge >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {fmt(marge)} € · {margePct.toFixed(1)} %
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 flex-1">Objectif marge</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                value={state.margeObjectif}
                onChange={e => onChange({ ...state, margeObjectif: parseFloat(e.target.value) || 0 })}
                className="w-12 px-1.5 py-0.5 text-xs border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-400"
              />
              <span className="text-xs text-gray-400">%</span>
            </div>
          </div>
          {state.margeObjectif > 0 && (
            <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium ${
              margeDelta >= 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
            }`}>
              {margeDelta >= 0
                ? <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                : <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-shrink-0" />
              }
              {margeDelta >= 0
                ? `Objectif atteint (+${margeDelta.toFixed(1)} pts)`
                : `Sous l'objectif (${margeDelta.toFixed(1)} pts)`
              }
            </div>
          )}
        </div>

        {/* Écart vs budget client */}
        {ecartBudget !== null && (
          <>
            <div className="border-t border-gray-200" />
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">Budget client</span>
                <span className="text-xs text-gray-600 tabular-nums font-medium">{fmt(budgetNum)} €</span>
              </div>
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium ${
                ecartBudget <= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
              }`}>
                {ecartBudget <= 0
                  ? <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  : <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                }
                {ecartBudget <= 0
                  ? `Dans le budget (−${fmt(Math.abs(ecartBudget))} €)`
                  : `Dépassement +${fmt(ecartBudget)} €`
                }
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FeuilleDeVentePanel;
